import React, { useRef, useState, useEffect } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Button } from "components/button"; // Replace with your button component
import { useTranslation } from 'react-i18next';

const SignatureCapture = ({ onSave, type, close, ...props }) => {
  const { t } = useTranslation();
  const tBase = "pages.unprotected.estimate.signatureCapture";
  const tr = (key) => t(`${tBase}.${key}`);

  const isInitial = type === "initials"; 
  const isPrintName = type === "printName";
  const isSignature = type === "signature";

  const [useText, setUseText] = useState(!isSignature); 
  const [text, setText] = useState(""); 
  const [savable, setSavable] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 400, height: 160 });
  const sigCanvas = useRef(null); // Reference for signature canvas



  useEffect(() => {
    const updateCanvasSize = () => {
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      const isLandscape = screenWidth > screenHeight;

      let newWidth, newHeight;

      if (isInitial) {
        // For initials, use 40% of screen width capped at 200px (landscape or portrait)
        newWidth = Math.min(screenWidth * 0.4, 200);
        newHeight = newWidth;
      } else {
        // For signatures:
        if (screenWidth < 480) {
          // For very small screens, in portrait use 90%, in landscape use 95%
          newWidth = isLandscape ? screenWidth * 0.95 : screenWidth * 0.9;
        } else if (screenWidth < 768) {
          // For tablets: in portrait use 80%, in landscape use 90%
          newWidth = isLandscape ? screenWidth * 0.9 : screenWidth * 0.8;
        } else {
          // For larger screens, use 70% of screen width, capped at 700px
          newWidth = isLandscape ? screenWidth * 0.95 : Math.min(screenWidth * 0.7, 700);
        }
        newHeight = newWidth / 6; // maintain 6:1 ratio
      }

      setCanvasSize({ width: newWidth, height: newHeight });
    };

    // Set size on load
    updateCanvasSize();

    // Update size on window resize
    window.addEventListener("resize", updateCanvasSize);
    return () => window.removeEventListener("resize", updateCanvasSize);
  }, [isInitial]);

  const isCanvasEmpty = () => {
    return sigCanvas.current && sigCanvas.current.isEmpty();
  };

  useEffect(() => {
    if (isPrintName) {
      setUseText(true); 
    }
  }, [isPrintName]);

  useEffect(() => {
    if (useText) {
      setSavable(text.trim() !== ""); 
    } else {
      setSavable(!isCanvasEmpty()); 
    }
  }, [text, useText]);

  
  // Function to save signature
  const handleSave = async () => {
    if (useText) {
      await onSave(text); // Save text signature
    } else {
      if (isCanvasEmpty()) return;
      const image = sigCanvas.current.getCanvas().toDataURL("image/png");
      await onSave(image); 
    }
    close();
  };

  // Function to clear signature
  const handleClear = () => {
    setText(""); 
    if (sigCanvas.current) sigCanvas.current.clear();
    setSavable(false);
    onSave(""); 
  };

  const canHeight = `${canvasSize.height}px`;
  const canWidth = `${canvasSize.width}px`;

  return (
    <div className="flex flex-col items-center w-full">
      {/* Toggle between text input and drawing */}
      {!isPrintName && (
        <div className="flex justify-center gap-4 mt-2">
          <button
            className={`px-3 py-1 ${
              useText
                ? "bg-blue-500 text-white"
                : "bg-gray-300 hover:bg-blue-500 hover:text-white"
            }`}
            onClick={() => {
              setText("");
              setUseText(true);
            }}
          >
            {isInitial ? tr("Type Initial") : tr("Type Signature")}
          </button>
          <button
            className={`px-3 py-1 ${
              !useText
                ? "bg-blue-500 text-white"
                : "bg-gray-300 hover:bg-blue-500 hover:text-white"
            }`}
            onClick={() => {
              setText("");
              setUseText(false);
            }}
          >
            {isInitial ? tr("Draw Initials") : tr("Draw Signature")}
          </button>
        </div>
      )}
      {/* Signature Input Field (Text) */}
      {useText ? (
        <input
          type="text"
          value={text}
          // onChange={(e) => setText(e.target.value)}
          onChange={(e) => {
            if (isInitial) {
              e.target.value = e.target.value.toUpperCase();
            }

            setText(e.target.value);
          }}
          className="border p-2 w-full text-center text-lg mt-2"
          placeholder={
            isInitial ? tr("Type your initials") : tr("Type your signature")
          }
          maxLength={isInitial ? 2 : 50}
        />
      ) : (
        <div className={`
          border border-gray-400 mt-2
        `}
          style={
            { width: canWidth, height: canHeight }
          }
        >
          <SignatureCanvas
            ref={sigCanvas}
            penColor="black"
            canvasProps={{
              width: canvasSize.width,
              height: canvasSize.height,
              className: "sigCanvas",
            }}
            onEnd={() => setSavable(!isCanvasEmpty())}
          />
        </div>
      )}

      {/* Signature Actions */}
      <div className="flex justify-between w-full mt-4">
        <Button
          onClick={close}
          className="bg-red-500 text-white px-4 py-2 rounded"
        >
          {tr("Cancel")}
        </Button>
        <div className="space-x-2">
          <Button
            onClick={handleClear}
            className="bg-red-500 text-white px-4 py-2 rounded"
          >
            {tr("Clear")}
          </Button>
          <Button
            primary={savable}
            onClick={handleSave}
            className="bg-green-500 text-white px-4 py-2 rounded"
          >
            {tr("Save")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SignatureCapture;
